// ---------------------------------------------------------------------------
// SSE clients for the merchant-scoped ask routes. Use raw fetch rather than the
// Hono RPC client because they need the response body as a stream.
//
// Parses SSE frames (event: / data: lines separated by double newlines) and
// calls onEvent(eventName, parsedData) for each complete frame.
//   query:  conversation, delta, finding, step_start, step_result, result, error
//   report: progress, report, error
// ---------------------------------------------------------------------------

import type { MerchantScope } from "@/api/scoped-merchant-client";
import { impersonationHeaders } from "@/lib/impersonation";
import { featureFlagHeaders } from "@/utils/feature-flag-overrides";

export type QueryBody = {
  question: string;
  conversation_id?: string | null;
  session_id?: string;
};

export type ReportBody = {
  templateId: string;
  additionalInputs?: Record<string, string>;
  session_id?: string;
};

type OnEvent = (eventType: string, data: unknown) => void;

const askPath = (scope: MerchantScope, route: "query" | "report") =>
  scope.workspaceId
    ? `/workspace/${scope.workspaceId}/merchant/${scope.merchantId}/ask/${route}`
    : `/merchant/${scope.merchantId}/ask/${route}`;

export function streamQuery(scope: MerchantScope, body: QueryBody, onEvent: OnEvent, signal?: AbortSignal) {
  return streamSSE(askPath(scope, "query"), body, onEvent, signal);
}

export function streamReport(scope: MerchantScope, body: ReportBody, onEvent: OnEvent, signal?: AbortSignal) {
  return streamSSE(askPath(scope, "report"), body, onEvent, signal);
}

async function streamSSE(path: string, body: unknown, onEvent: OnEvent, signal?: AbortSignal): Promise<void> {
  const res = await fetch(`${import.meta.env.VITE_API_URL}${path}`, {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...impersonationHeaders(),
      ...featureFlagHeaders(),
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) throw new Error(`Server error: ${res.status}`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let sseBuffer = "";

  const parseSSE = (chunk: string): Array<{ type: string; data: unknown }> => {
    sseBuffer += chunk;
    const events: Array<{ type: string; data: unknown }> = [];
    // SSE messages are separated by double newlines
    const parts = sseBuffer.split("\n\n");
    sseBuffer = parts.pop() ?? "";
    for (const part of parts) {
      let eventType = "message";
      let dataStr = "";
      for (const line of part.split("\n")) {
        if (line.startsWith("event: ")) eventType = line.slice(7).trim();
        else if (line.startsWith("data: ")) dataStr = dataStr ? dataStr + "\n" + line.slice(6) : line.slice(6);
      }
      if (dataStr) {
        try {
          events.push({ type: eventType, data: JSON.parse(dataStr) });
        } catch {
          /* skip malformed */
        }
      }
    }
    return events;
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    const events = parseSSE(decoder.decode(value, { stream: true }));
    for (const event of events) {
      onEvent(event.type, event.data);
    }
  }
}
